/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { QueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { ApartmentTypeDto, AuthenticateCustomerDto, CustomerSelectionDto } from 'api'
import {
  AddOnSelectorBackendApi,
  ApiPublicProjectIdCustomerCustomerIdGetRequest,
  ApiPublicProjectIdCustomerCustomerIdSelectionsGetRequest,
  ApiPublicProjectIdCustomerCustomerIdSelectionsRoomRoomIdPostRequest,
  ApiPublicProjectIdGetRequest,
  ApiPublicProjectIdProductProductIdInfoGetRequest,
} from 'api/apis/AddOnSelectorBackendApi'
import {
  ApiUsersAuthenticateProjectIdPostRequest,
  ApiUsersMeProjectIdGetRequest,
  UserRoutesApi,
} from 'api/apis/UserRoutesApi'
import { CustomerDto } from 'api/models/CustomerDto'
import { generateMail, GenerateMailParams } from 'utilities/generate-mail'
import { setLocalLoginRequest, setLocalToken } from 'utilities/use-local-data'
import { configuration } from './utilities'

const addOnSelectorBackendApi = new AddOnSelectorBackendApi(configuration)
const userRoutesAPI = new UserRoutesApi(configuration)
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: 60000,
      suspense: true,
      useErrorBoundary: true,
      // refetchOnWindowFocus: false,
      // refetchOnMount: false,
    },
  },
})

// After login, users need to start session
export function useStartSession() {
  return useMutation((params: { customerId: number; projectId: number }) =>
    addOnSelectorBackendApi.apiPublicProjectIdCustomerCustomerIdSelectionsStartPost(params)
  )
}

export const loginCustomer = (params: ApiUsersAuthenticateProjectIdPostRequest) =>
  userRoutesAPI.apiUsersAuthenticateProjectIdPost(params).then((data) => {
    const { projectId, authenticateCustomerModel } = params
    const { customer, token } = data
    setCustomer(customer, { projectId })
    // Save login credentials in local storage to be used when 401 is received from backend
    setLocalLoginRequest(authenticateCustomerModel)

    // Save token to be used when calling private api endpoint
    setLocalToken(token)
    return data
  })

export function useLoginCustomer() {
  return useMutation<
    AuthenticateCustomerDto,
    unknown,
    ApiUsersAuthenticateProjectIdPostRequest,
    unknown
  >(loginCustomer)
}

// Customer Queries
const CUSTOMER_KEY = 'customer'
type CustomerKey = [typeof CUSTOMER_KEY, ApiUsersMeProjectIdGetRequest]
export function getCustomerKey(params: { projectId: number }): CustomerKey
export function getCustomerKey(params: { projectId?: number }): CustomerKey | undefined
export function getCustomerKey(params: { projectId?: number }): CustomerKey | undefined {
  const { projectId } = params
  return projectId ? [CUSTOMER_KEY, { projectId }] : undefined
}

export const useCustomer = (params: { projectId?: number }) => {
  const queryKey = getCustomerKey(params)
  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, params] }) => userRoutesAPI.apiUsersMeProjectIdGet(params),
    enabled: !!queryKey,
  })
}

export const getCustomer = (params: { projectId: number }) =>
  queryClient.getQueryData<CustomerDto>(getCustomerKey(params))

export const setCustomer = (
  data: CustomerDto,
  params: {
    projectId: number
  }
) => queryClient.setQueryData<CustomerDto>(getCustomerKey(params), data)

export const prefetchCustomer = (params: { projectId: number }) =>
  queryClient.prefetchQuery(getCustomerKey(params), () =>
    userRoutesAPI.apiUsersMeProjectIdGet(params)
  )

// Project info queries
const PROJECT_KEY = 'project'
type ProjectKey = [typeof PROJECT_KEY, ApiPublicProjectIdGetRequest]
function getProjectKey(params: { projectId: number }): ProjectKey
function getProjectKey(params: { projectId?: number }): false | ProjectKey
function getProjectKey(params: { projectId?: number }): false | ProjectKey {
  const { projectId } = params
  return !!projectId && [PROJECT_KEY, { projectId }]
}

export const useProject = (params: { projectId: number }) => {
  const key = getProjectKey(params)
  return useQuery(key, () => addOnSelectorBackendApi.apiPublicProjectIdGet(params))
}

// Product info queries
const PRODUCT_KEY = 'product'
type ProductKey = [typeof PRODUCT_KEY, ApiPublicProjectIdProductProductIdInfoGetRequest]
function getProductKey(params: { projectId: number; productId: number }): ProductKey
function getProductKey(params: { projectId?: number; productId?: number }): false | ProductKey
function getProductKey(params: { projectId?: number; productId?: number }): false | ProductKey {
  const { projectId, productId } = params
  return !!projectId && !!productId && [PRODUCT_KEY, { projectId, productId }]
}

export const useProductInfo = (params: { projectId?: number; productId?: number }) => {
  const key = getProductKey(params)
  return useQuery(
    key === false ? [key] : key,
    () =>
      addOnSelectorBackendApi.apiPublicProjectIdProductProductIdInfoGet({
        productId: params.productId!,
        projectId: params.projectId!,
      }),
    { enabled: key !== false }
  )
}

export const prefetchProject = (params: { projectId: number }) => {
  return queryClient.prefetchQuery(getProjectKey(params), () =>
    addOnSelectorBackendApi.apiPublicProjectIdGet(params)
  )
}

// Apartment type queries
const APARTMENT_KEY = 'apartmentType'
type ApartmentKey = [typeof APARTMENT_KEY, ApiPublicProjectIdCustomerCustomerIdGetRequest]
export function getApartmentKey(requestParameters: {
  projectId: number
  customerId: number
}): ApartmentKey {
  const { projectId, customerId } = requestParameters
  return [APARTMENT_KEY, { projectId, customerId }]
}

/*
 * Filter out all rooms which do not have any
 * products and all products which does not have any options
 */
export const getFilteredCustomerInfo = (
  requestParameters: ApiPublicProjectIdCustomerCustomerIdGetRequest
): Promise<ApartmentTypeDto> =>
  addOnSelectorBackendApi
    .apiPublicProjectIdCustomerCustomerIdGet(requestParameters)
    .then((data) => ({
      ...data,
      roomTypes: data.roomTypes
        .map((roomType) => ({
          ...roomType,
          name: roomType.name ?? 'No name',
          productTypes: roomType.productTypes
            .filter((productType) => productType.products.length > 0)
            .map((p) => ({ ...p, products: p.products.sort((a, b) => a.price - b.price) })),
        }))
        .filter((roomType) => roomType.productTypes.length > 0),
    }))

export const useGetApartmentInfo = (requestParameters: {
  projectId?: number
  customerId?: number
}) => {
  const { projectId, customerId } = requestParameters
  if (!projectId || !customerId) throw new Error('Missing params: projectId or customerId')
  const key = getApartmentKey({ projectId, customerId })
  return useQuery(key, () => getFilteredCustomerInfo({ projectId, customerId }))
}

export const prefetchApartmentInfo = (requestParameters: {
  projectId: number
  customerId: number
}) => {
  const key = getApartmentKey(requestParameters)
  return queryClient.prefetchQuery(key, () => getFilteredCustomerInfo(requestParameters))
}

// Selections queries
const SELECTIONS_KEY = 'selections'
type SelectionsKey = [
  typeof SELECTIONS_KEY,
  ApiPublicProjectIdCustomerCustomerIdSelectionsGetRequest
]
function getSelectionKey(params: {
  customerId: ApiPublicProjectIdCustomerCustomerIdSelectionsGetRequest['customerId']
  projectId: ApiPublicProjectIdCustomerCustomerIdSelectionsGetRequest['projectId']
}): SelectionsKey {
  const { projectId, customerId } = params
  return [SELECTIONS_KEY, { projectId, customerId }]
}

type SaveSelectionParams = Partial<
  Pick<
    ApiPublicProjectIdCustomerCustomerIdSelectionsRoomRoomIdPostRequest,
    'customerId' | 'projectId'
  >
>
type MutateSelectionParams = Required<
  Omit<CustomerSelectionDto, 'productTypeId'> & {
    productTypeId: NonNullable<CustomerSelectionDto['productTypeId']>
  }
>

export const useSaveSelection = (params: SaveSelectionParams) => {
  const { customerId, projectId } = params
  if (!projectId || !customerId) throw new Error('Missing params: projectId and/or customerId')
  return useMutation(
    ['save-selection', customerId, projectId],
    (customerSelectionDto: MutateSelectionParams) => {
      const { roomId } = customerSelectionDto
      return addOnSelectorBackendApi
        .apiPublicProjectIdCustomerCustomerIdSelectionsRoomRoomIdPost({
          customerId,
          projectId,
          roomId,
          customerSelectionDto,
        })
        .then(() => {
          const key = getSelectionKey({
            customerId,
            projectId,
          })
          queryClient.refetchQueries(key)
        })
    },
    {
      onMutate: (customerSelectionDto) => {
        // Because we throw in mutation if one of customerId or projectId is undefiend, we can assert that they are non-null and non-undefined
        const key = getSelectionKey({
          customerId: customerId,
          projectId: projectId,
        })
        queryClient.cancelQueries(key)

        // Save the list of selected options incase of error when saving
        const previousSelections = queryClient.getQueryData<Array<CustomerSelectionDto>>(key)

        // Optimistically update list of selected options
        queryClient.setQueryData<Array<CustomerSelectionDto>>(key, (prevProducts = []) => {
          const index = prevProducts.findIndex(
            (prevProduct) =>
              prevProduct.productTypeId === customerSelectionDto.productTypeId &&
              prevProduct.roomId === customerSelectionDto.roomId
          )
          const copy = prevProducts.slice(0)
          copy.splice(index, 1, customerSelectionDto)
          return copy
        })

        // Return previous list of selection to be used in onError callback
        return previousSelections
      },
      onError: (err, variables, previousValue) => {
        const key = getSelectionKey({
          customerId: customerId,
          projectId: projectId,
        })
        queryClient.setQueryData(key, previousValue)
      },
    }
  )
}

export const useGetSelections = (params: {
  projectId?: ApiPublicProjectIdCustomerCustomerIdSelectionsGetRequest['projectId']
  customerId?: ApiPublicProjectIdCustomerCustomerIdSelectionsGetRequest['customerId']
}) => {
  const { projectId, customerId } = params
  if (!projectId || !customerId) throw new Error('Missing params: projectId and/or customerId')
  const key = getSelectionKey({ projectId, customerId })
  return useQuery(
    key,
    () => {
      return addOnSelectorBackendApi.apiPublicProjectIdCustomerCustomerIdSelectionsGet({
        projectId,
        customerId,
      })
    },
    {
      suspense: false,
    }
  )
}

export const useFinishSelection = (params: { customerId?: number; projectId?: number }) => {
  const { customerId, projectId } = params

  if (!customerId || !projectId) {
    throw Error('Either customerId or projectId were not provided to useSaveSelection hook')
  }

  return useMutation((params: GenerateMailParams) => {
    return generateMail(params).then(async (emailBody) => {
      const result =
        await addOnSelectorBackendApi.apiPublicProjectIdCustomerCustomerIdSelectionsFinishPost({
          customerId,
          finishSelectionModel: { emailBody },
          projectId,
        })
      if (result.hasErrors) throw result.message
      return result
    })
  })
}
